// frontend/src/utils/excelExport.ts
import * as XLSX from 'xlsx';

export interface ExcelExportData {
  headers: string[];
  rows: (string | number)[][];
  filename: string;
  durationColumns?: number[]; // Column indices that should be formatted as durations
}

/**
 * Converts data to Excel format and triggers download
 */
export const downloadExcel = (data: ExcelExportData): void => {
  const { headers, rows, filename, durationColumns = [] } = data;

  // Create worksheet data with headers and rows
  const wsData = [headers, ...rows];

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Apply duration formatting to specified columns
  if (durationColumns.length > 0) {
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

    for (let row = 1; row <= range.e.r; row++) { // Start from 1 to skip header
      durationColumns.forEach(col => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];

        if (cell && typeof cell.v === 'number') {
          // Format as [h]:mm to allow hours > 24 and show negative values
          cell.z = '[h]:mm';
          cell.t = 'n';
        }
      });
    }
  }

  // Calculate column widths based on content
  const colWidths = headers.map((header, i) => {
    const headerLength = header.length;
    const maxContentLength = Math.max(
      ...rows.map(row => String(row[i]).length),
      headerLength
    );
    return { wch: Math.min(Math.max(maxContentLength + 2, 10), 50) };
  });
  ws['!cols'] = colWidths;

  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Statistiken');

  // Generate Excel file and trigger download
  XLSX.writeFile(wb, filename);
};

/**
 * Converts time in minutes to Excel duration format (fraction of a day)
 * Excel stores durations as fractions: 1 day = 1, 1 hour = 1/24, 1 minute = 1/1440
 */
export const formatTimeForExcel = (minutes: number): number => {
  return minutes / 1440; // Convert minutes to fraction of a day
};

/**
 * Formats date for Excel export in ISO format (YYYY-MM-DD)
 */
export const formatDateForExcel = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
